import React from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, BarChart3, Zap, Users, Smartphone, CheckCircle, ArrowRight, Star, UserCircle } from 'lucide-react';

const benefits = [
  { icon: TrendingDown, title: 'Reduce Delinquency', description: 'Cut late payments from 30-40% to near-zero.', gradient: 'from-red-500 to-orange-500' },
  { icon: BarChart3, title: 'Real-Time Oversight', description: 'See payments, maintenance, and messages instantly.', gradient: 'from-blue-500 to-cyan-500' },
  { icon: Users, title: 'Tenant Satisfaction', description: 'Faster responses mean longer-term tenants.', gradient: 'from-emerald-500 to-green-500' },
  { icon: Smartphone, title: 'Mobile-First Platform', description: 'Manage everything from your phone, anytime, anywhere.', gradient: 'from-purple-500 to-pink-500' },
  { icon: Zap, title: 'Efficiency Boost', description: 'Automate collections, reports, and tenant management.', gradient: 'from-yellow-500 to-amber-500' },
  { icon: CheckCircle, title: 'Full Accountability', description: 'Digital trail for every transaction and interaction.', gradient: 'from-teal-500 to-cyan-500' },
];

const dashboardFeatures = [
  'Multi-property portfolio view',
  'Payment collection automation',
  'Maintenance ticket system',
  'Tenant communication hub',
  'Financial reporting & analytics',
  'Revenue tracking & forecasting',
  'Mobile-first dashboard & reporting',
  'Automated late fee calculation',
];

const roiStats = [
  { number: '40% ↓', label: 'Reduce delinquency', gradient: 'from-red-400 to-orange-400' },
  { number: '3-5x', label: 'ROI within 6 months', gradient: 'from-blue-400 to-cyan-400' },
  { number: '20+ hrs', label: 'Saved per week', gradient: 'from-emerald-400 to-green-400' },
];

const testimonial = {
  name: 'James',
  location: 'Mombasa',
  role: 'Property Manager',
  text: 'Delinquency was eating into my profits. YAWOTE automated collections and reduced late payments from 40% to 8%. Best decision ever.',
};

export default function ManagersPage() {
  return (
    <div className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-medium mb-6">
            For Property Managers
          </span>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Reduce Delinquency.{' '}
            <span className="text-gradient-secondary">Increase Retention.</span>
            <br />
            <span className="text-gradient">Unlock New Revenue.</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Stop wasting hours on manual collection and messy spreadsheets. YAWOTE gives you 
            a single dashboard to manage your entire portfolio.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {benefits.map((benefit, idx) => {
            const IconComponent = benefit.icon;
            return (
              <motion.div
                key={idx}
                className="group relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
                <div className="relative card">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <IconComponent className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Dashboard Feature */}
        <motion.div
          className="rounded-2xl p-12 mb-20 relative overflow-hidden"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/20 rounded-2xl"></div>
          <div className="relative">
            <h2 className="text-3xl font-bold text-white mb-4">The Landlord Dashboard</h2>
            <p className="text-gray-300 mb-8">
              View all properties. See who has paid. Reassign maintenance. Generate financial reports. 
              All in real-time.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {dashboardFeatures.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-green-400" size={18} />
                  </div>
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ROI Stats */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {roiStats.map((stat, idx) => (
            <motion.div
              key={idx}
              className="card text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              viewport={{ once: true }}
            >
              <div className={`text-4xl font-bold mb-2 bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                {stat.number}
              </div>
              <p className="text-gray-400">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Testimonial */}
        <motion.div
          className="card max-w-3xl mx-auto mb-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="flex gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <p className="text-gray-300 text-lg italic mb-6">"{testimonial.text}"</p>
          <div className="flex items-center gap-3 pt-4 border-t border-white/5">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center ring-2 ring-purple-500/20">
              <UserCircle className="text-white" size={24} />
            </div>
            <div>
              <p className="font-bold text-white">{testimonial.name}</p>
              <p className="text-gray-400 text-sm">{testimonial.role} • {testimonial.location}</p>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          viewport={{ once: true }}
        >
          <a href="https://manager.yawote.co.ke" target="_blank" rel="noopener noreferrer">
            <button className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-4 group">
              Get Started
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </a>
        </motion.div>
      </div>
    </div>
  );
}
